import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';

const useStyles = makeStyles(() => ({
  paper: {
    width: '100%',
    maxWidth: 500,
    borderRadius: 20,
    border: '1px solid #e1e7e4',
    padding: 28,
    boxShadow: 'none',
  },
  headRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 14,
  },
  iconChip: {
    flex: 'none',
    width: 44,
    height: 44,
    borderRadius: '50%',
    backgroundColor: '#f2fbf7',
    border: '1px solid #cfeadd',
    color: '#0f8461',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    margin: 0,
    fontFamily: "'Inter Tight', sans-serif",
    fontSize: 21,
    lineHeight: 1.25,
    fontWeight: 700,
    letterSpacing: '-.01em',
    color: '#16141d',
  },
  body: {
    margin: '8px 0 0',
    fontSize: 13.8,
    lineHeight: 1.55,
    color: '#5f5d75',
  },
  bodyStrong: {
    color: '#16141d',
    fontWeight: 600,
  },
  closeButton: {
    flex: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    borderRadius: 8,
    border: 'none',
    background: 'none',
    color: '#5b6661',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: '#eef2f0',
      color: '#16141d',
    },
  },
  actionRow: {
    marginTop: 22,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
    gap: 10,
  },
  cancelButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 44,
    padding: '0 20px',
    border: '1.5px solid #d7dbe0',
    borderRadius: 10,
    backgroundColor: '#fff',
    color: '#16141d',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    '&:hover': {
      borderColor: '#b6bcc4',
      backgroundColor: '#f6f8f7',
    },
  },
  confirmButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: 44,
    padding: '0 22px',
    border: 'none',
    borderRadius: 10,
    backgroundColor: '#0f8461',
    color: '#fff',
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    '&:hover': {
      backgroundColor: '#0b6b4e',
    },
    '&:disabled': {
      backgroundColor: '#e7ebe9',
      color: '#9aa5a0',
      cursor: 'not-allowed',
    },
  },
}));

const CardIcon = () => (
  <svg width={21} height={21} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="5" width="20" height="14" rx="2.5" />
    <path d="M2 10h20" />
  </svg>
);

const CloseIcon = () => (
  <svg width={16} height={16} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

export const SetDefaultPaymentMethodDialog = (
  { open, methodLabel, oldMethodLabel, accountName, loading, onCancel, onConfirm }: {
    open: boolean;
    methodLabel: string;
    oldMethodLabel: string;
    accountName?: string;
    loading: boolean;
    onCancel: () => void;
    onConfirm: () => void;
  },
) => {
  const classes = useStyles();

  return (
    <Dialog
      id="set-default-payment-method-dialog"
      open={open}
      onClose={onCancel}
      classes={{ paper: classes.paper }}
      BackdropProps={{ sx: { backgroundColor: 'rgba(22,20,29,.42)' } }}
    >
      <Box className={classes.headRow}>
        <Box component="span" className={classes.iconChip}>
          <CardIcon />
        </Box>
        <Box flex={1} minWidth={0}>
          <Typography component="h2" className={classes.title}>
            <FormattedMessage
              id="page.payment.default_dialog.title"
              values={{ method: methodLabel }}
            />
          </Typography>
          <Typography className={classes.body}>
            <FormattedMessage
              id="page.payment.default_dialog.body"
              values={{
                oldMethod: <span className={classes.bodyStrong}>{oldMethodLabel}</span>,
                accountName: <span className={classes.bodyStrong}>{accountName}</span>,
              }}
            />
          </Typography>
        </Box>
        <button type="button" aria-label="Close" className={classes.closeButton} onClick={onCancel}>
          <CloseIcon />
        </button>
      </Box>
      <Box className={classes.actionRow}>
        <button type="button" className={classes.cancelButton} onClick={onCancel} disabled={loading}>
          <FormattedMessage id="page.payment.default_dialog.cancel" />
        </button>
        <button
          type="button"
          id="set-default-payment-method-confirm-btn"
          className={classes.confirmButton}
          onClick={onConfirm}
          disabled={loading}
        >
          <FormattedMessage id="page.payment.default_dialog.confirm" />
        </button>
      </Box>
    </Dialog>
  );
};
